import React, { useRef, useState } from 'react';
import { noop } from 'lodash';
import { translate } from '@fiverr-private/i18n-react';
import { Button } from '@fiverr-private/button';
import {
    Modal,
    ModalBody,
    ModalCloseBtn,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalTrigger,
} from '@fiverr-private/overlay';
import { Heading } from '@fiverr-private/typography';
import { Collection, CollectionFormFields, RawCollection, ResourceType } from '../../types';
import { useCreateCollection } from '../../hooks/useCreateCollection';
import { useSaveCollection } from '../../hooks/useSaveCollection';
import { useCollectionsContext } from '../../context/CollectionsContext';
import { sendClickEditListEvent, sendClickCreateNewListEvent } from '../../utils/events/freelancersListEvents';
import { AddEditListForm } from './AddEditListForm';

interface AddEditListModalProps {
    collection?: Collection;
    trigger?: React.ReactNode;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    resourceType?: ResourceType;
    resourceId?: string;
    onSuccess?: (collection: RawCollection) => void;
    onError?: (error?: Error) => void;

    /**
     * By default, shows for edit, not for create. Can be specified to always be true or false.
     * */
    showDescription?: boolean | 'auto';
}

export const AddEditListModal = ({
    collection,
    trigger,
    isOpen: controlledIsOpen,
    onOpenChange,
    resourceType,
    resourceId,
    onSuccess = noop,
    onError = noop,
    showDescription = 'auto',
}: AddEditListModalProps) => {
    const collectionBeforeChanges = Object.assign({}, collection);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const isEditing = !!collection?.id;
    const { bi } = useCollectionsContext();

    const handleSuccess = (collection: RawCollection) => {
        onSuccess(collection);
        handleClose();
    };

    const { isLoading: isSaving, saveCollection } = useSaveCollection({
        bi,
        onError,
        onSuccess: handleSuccess,
        id: collection?.id,
    });
    const { isLoading: isCreating, createCollection } = useCreateCollection({
        resourceType,
        resourceId,
        bi,
        onError,
        onSuccess: handleSuccess,
    });
    const isSubmitting = isSaving || isCreating;
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

    const handleOpen = () => {
        if (isControlled) {
            onOpenChange?.(true);
        } else {
            setInternalIsOpen(true);
        }

        sendEvents();
    };

    const sendEvents = () => {
        const props = { biContext: bi };

        return isEditing ? sendClickEditListEvent(props) : sendClickCreateNewListEvent(props);
    };

    const handleClose = () => {
        if (isControlled) {
            onOpenChange?.(false);
        } else {
            setInternalIsOpen(false);
        }
    };

    const onSubmit = async (collection: CollectionFormFields): Promise<void> => {
        await (isEditing ? saveCollection(collection, collectionBeforeChanges) : createCollection(collection));
    };

    return (
        <Modal isOpen={isOpen} onOpen={handleOpen} onClose={handleClose} fullScreenBreakpoint="sm">
            <ModalTrigger>{trigger}</ModalTrigger>
            <ModalContent size="sm">
                <ModalHeader>
                    <Heading as="h4">
                        {isEditing
                            ? translate('collect_actions.add_edit_list_modal.edit_title')
                            : translate('collect_actions.add_edit_list_modal.create_title')}
                    </Heading>
                    <ModalCloseBtn />
                </ModalHeader>
                <ModalBody>
                    <AddEditListForm
                        formRef={formRef}
                        onSubmit={onSubmit}
                        collection={collection}
                        showDescription={showDescription}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button intent="secondary" variant="ghost" onClick={handleClose}>
                        {translate('collect_actions.add_edit_list_modal.cancel')}
                    </Button>
                    <Button type="submit" loading={isSubmitting} onClick={() => formRef.current?.requestSubmit()}>
                        {isEditing
                            ? translate('collect_actions.add_edit_list_modal.save')
                            : translate('collect_actions.add_edit_list_modal.create')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
